import axios, { AxiosError } from 'axios';
import type { ApiErrorBody, ApiSuccess } from '@rg/shared';

/** All API calls go through the same-origin /api path (Vite proxy in dev, nginx in Docker). */
export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status?: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  if (err instanceof AxiosError) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error)
      return new ApiError(
        body.error.message,
        body.error.code,
        err.response?.status,
        body.error.details,
      );
    if (err.code === 'ECONNABORTED')
      return new ApiError('The request timed out. Please try again.', 'TIMEOUT');
    return new ApiError(
      'We couldn’t reach the server. Check your connection and try again.',
      'NETWORK_ERROR',
      err.response?.status,
    );
  }
  return new ApiError('Something went wrong.', 'UNKNOWN');
}

/** Unwraps the `{ success, data }` envelope and normalises errors. */
export async function unwrap<T>(p: Promise<{ data: ApiSuccess<T> }>): Promise<T> {
  try {
    return (await p).data.data;
  } catch (err) {
    throw toApiError(err);
  }
}
